// src/utils/DBFetchers.js
import axios from "axios";
import { encryptLoginCredentials } from "./loginEncryption";

var apiUri = process.env.REACT_APP_API_URI;

// DATA FUNCTIONS
const getApiUrl = (category) =>
  `${(apiUri || "").replace(/\/+$/, "")}/${String(category).replace(
    /^\/+/,
    "",
  )}`;

const getToken = () => {
  return localStorage.getItem("token");
};

const getAuthData = (type) => {
  const token = getToken();
  const requestData = {
    method: type,
    headers: { Authorization: `Bearer ${token}` },
  };
  return requestData;
};

const contentAuthData = (type, body) => {
  const token = getToken();
  const requestData = {
    method: type,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
  if (body) requestData["body"] = JSON.stringify(body);
  return requestData;
};

const publicContentData = (type, body) => {
  const requestData = {
    method: type,
    headers: {
      "Content-Type": "application/json",
    },
  };
  if (body) requestData["body"] = JSON.stringify(body);
  return requestData;
};

async function parsePublicResponseData(response) {
  if (response.status === 204) return null;

  const contentType = response.headers.get("content-type") || "";
  const text = await response.text();

  if (!text) return null;
  if (!contentType.includes("application/json")) return text;

  return JSON.parse(text);
}

async function getResponseData(response, data) {
  const { status } = response;
  if (status >= 200 && status < 300) {
    return data;
  }
  return Promise.reject(data);
}

// GENERIC DB FETCHERS
async function dbGet(category) {
  const requestData = getAuthData("GET");
  const response = await fetch(`${apiUri}/${category}`, requestData);
  const data = await response.json();

  return await getResponseData(response, data);
}

async function dbPublicGet(category) {
  const response = await fetch(getApiUrl(category), { method: "GET" });
  const data = await parsePublicResponseData(response);

  return await getResponseData(response, data);
}

function getFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return null;

  const utf8Filename = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Filename?.[1]) return decodeURIComponent(utf8Filename[1]);

  const filename = contentDisposition.match(/filename="?([^";]+)"?/i);
  return filename?.[1] || null;
}

async function dbDownloadFile(category) {
  const requestData = getAuthData("GET");
  const response = await fetch(`${apiUri}/${category}`, requestData);

  if (response.status >= 200 && response.status < 300) {
    return {
      blob: await response.blob(),
      filename: getFilenameFromContentDisposition(
        response.headers.get("content-disposition"),
      ),
    };
  }

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  return Promise.reject(data);
}

async function dbPostDownloadFile(category, postData) {
  const requestData = contentAuthData("POST", postData);
  const response = await fetch(`${apiUri}/${category}`, requestData);

  if (response.status >= 200 && response.status < 300) {
    return {
      blob: await response.blob(),
      filename: getFilenameFromContentDisposition(
        response.headers.get("content-disposition"),
      ),
    };
  }

  const contentType = response.headers.get("content-type") || "";
  const data = contentType.includes("application/json")
    ? await response.json()
    : await response.text();

  return Promise.reject(data);
}

async function dbPut(category, putData) {
  const requestData = contentAuthData("PUT", putData);
  const response = await fetch(`${apiUri}/${category}`, requestData);
  const data = await response.json();

  return await getResponseData(response, data);
}

async function dbPublicPut(category, putData) {
  const requestData = publicContentData("PUT", putData);
  const response = await fetch(getApiUrl(category), requestData);
  const data = await parsePublicResponseData(response);

  return await getResponseData(response, data);
}

async function dbPutWithFormData(category, formData) {
  // Reutiliza auth header (Bearer) sin Content-Type JSON
  const authHeaders = contentAuthData().headers || {};
  const { ["Content-Type"]: _ct, ...headers } = authHeaders;

  const response = await fetch(`${apiUri}/${category}`, {
    method: "PUT",
    headers,
    body: formData,
  });

  let data = null;
  if (response.status !== 204) {
    const ct = response.headers.get("content-type") || "";
    try {
      data = ct.includes("application/json")
        ? await response.json()
        : await response.text();
    } catch {
      data = null;
    }
  }

  return await getResponseData(response, data);
}

async function dbPutWithHeaders(category, putData, extraHeaders = {}) {
  const requestData = contentAuthData("PUT", putData);
  requestData.headers = { ...(requestData.headers || {}), ...extraHeaders };

  const response = await fetch(`${apiUri}/${category}`, requestData);
  const data = await response.json();
  return await getResponseData(response, data);
}

async function dbPatch(category, patchData) {
  const requestData = contentAuthData("PATCH", patchData);
  const response = await fetch(`${apiUri}/${category}`, requestData);
  const data = await response.json();
  return await getResponseData(response, data);
}


async function dbPost(category, postData) {
  const requestData = contentAuthData("POST", postData);
  const response = await fetch(`${apiUri}/${category}`, requestData);
  const data = response.status === 204 ? null : await response.json();

  return await getResponseData(response, data);
}

async function dbPostWithFormData(category, formData) {
  const authHeaders = contentAuthData().headers || {};
  const { ["Content-Type"]: _ct, ...headers } = authHeaders;

  const response = await fetch(`${apiUri}/${category}`, {
    method: "POST",
    headers,
    body: formData,
  });

  let data = null;
  if (response.status !== 204) {
    const ct = response.headers.get("content-type") || "";
    try {
      data = ct.includes("application/json")
        ? await response.json()
        : await response.text();
    } catch {
      data = null; // cuerpo vacío o ilegible
    }
  }

  return await getResponseData(response, data);
}

async function dbDelete(category, deleteData) {
  const requestData = contentAuthData("DELETE", deleteData);
  const response = await fetch(`${apiUri}/${category}`, requestData);
  const data = await response.json();

  return await getResponseData(response, data);
}

async function dbUploadFile(file, endpoint) {
  let formData = new FormData();
  formData.append("file", file);
  const token = getToken();

  let toSend = axios.create({
    baseURL: apiUri,
    headers: {
      "Content-type": "application/json",
    },
  });
  let response = await toSend.post(endpoint, formData, {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "multipart/form-data",
    },
  });
  const { data } = response;
  return await getResponseData(response, data);
}

// SPECIFIC DB FETCHERS

async function dbDisableEnable(endpoint, id, command) {
  const requestData = contentAuthData("PUT");
  const response = await fetch(
    `${apiUri}/${endpoint}/${id}/${command}`,
    requestData,
  );
  const data = await response.json();
  return await getResponseData(response, data);
}

/* This function exists because endpoint is long */
async function getInscriptionsByPatientRut(patient_rut) {
  const requestData = getAuthData("GET");
  const programId = localStorage.getItem("programId");
  const response = await fetch(
    `${apiUri}/inscriptions/presentations/${patient_rut}/${programId}`,
    requestData,
  );
  const data = await response.json();

  return await getResponseData(response, data);
}

async function Login(userData) {
  const encryptedCredentials = await encryptLoginCredentials(apiUri, {
    username: userData.username,
    password: userData.password,
  });
  const requestData = {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(encryptedCredentials),
  };

  const response = await fetch(`${apiUri}/login/encrypted`, requestData);
  const data = await response.json();

  if (response.status === 429) {
    return Promise.reject({ ...data, httpStatus: response.status });
  }

  return await getResponseData(response, data);
}

async function LoginWithPin(payload) {
  const response = await fetch(`${apiUri}/login/with_pin`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload),
  });
  const data = await response.json();

  return await getResponseData(response, data);
}

async function Reset(username) {
  var encodedData = Buffer.from(`${username}:`).toString("base64");
  const requestData = {
    method: "POST",
    headers: {
      Authorization: `Basic ${encodedData}`,
    },
  };
  const response = await fetch(`${apiUri}/password-reset`, requestData);
  const data = await response.json();

  return await getResponseData(response, data);
}

export {
  getInscriptionsByPatientRut,
  dbDownloadFile,
  dbPostDownloadFile,
  dbGet,
  dbPublicGet,
  dbPut,
  dbPublicPut,
  dbPutWithHeaders,
  dbPutWithFormData,
  dbDelete,
  dbPost,
  dbPatch,
  dbPostWithFormData,
  dbDisableEnable,
  dbUploadFile,
  Login,
  Reset,
  LoginWithPin,
};
